import { useState, useEffect } from 'react';
import { useOutletContext, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  HardHat, MapPin, Calendar, Euro, Users, Clock,
  GripVertical, TrendingUp, TrendingDown, Plus, FileText
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

export default function ChantiersPage() {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [stages, setStages] = useState([]);
  const [pipelineData, setPipelineData] = useState({});
  const [loading, setLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState(null);

  useEffect(() => {
    fetchPipeline();
  }, []);

  const fetchPipeline = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/batiment/pipeline/chantier`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStages(data.stages);
        setPipelineData(data.data);
      }
    } catch (error) {
      console.error('Error fetching pipeline:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, chantier, stageId) => {
    setDraggedItem({ chantier, fromStage: stageId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, toStageId) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.fromStage === toStageId) {
      setDraggedItem(null);
      return;
    }

    const token = localStorage.getItem('client_token');
    const { chantier, fromStage } = draggedItem;

    // Optimistic update
    setPipelineData(prev => {
      const newData = { ...prev };
      newData[fromStage] = newData[fromStage].filter(c => c.id !== chantier.id);
      newData[toStageId] = [...(newData[toStageId] || []), { ...chantier, pipeline_stage: toStageId }];
      return newData;
    });

    try {
      const response = await fetch(
        `${API_URL}/api/batiment/pipeline/chantier/move?chantier_id=${chantier.id}&new_stage=${toStageId}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Chantier déplacé');
      } else {
        fetchPipeline();
        toast.error('Erreur lors du déplacement');
      }
    } catch (error) {
      fetchPipeline();
      toast.error('Erreur lors du déplacement');
    }

    setDraggedItem(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const getRentabilityIcon = (rentability) => {
    if (rentability === 'high') return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (rentability === 'low') return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <TrendingUp className="w-4 h-4 text-amber-500" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  const totalChantiers = Object.values(pipelineData).flat().length;

  return (
    <div className="space-y-6" data-testid="chantiers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-rm-text">Pipeline Chantiers</h1>
          <p className="text-rm-text-secondary">Suis l'avancement de tes travaux</p>
        </div>
        <div className="text-sm text-rm-text-secondary">
          {totalChantiers} chantier{totalChantiers > 1 ? 's' : ''} en cours
        </div>
      </div>

      {totalChantiers === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-12 text-center">
          <HardHat className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-rm-text-secondary mb-2">Aucun chantier en cours</p>
          <p className="text-sm text-slate-400">
            Les chantiers sont créés automatiquement lorsqu'un devis est accepté
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {stages.map((stage) => {
              const chantiers = pipelineData[stage.id] || [];

              return (
                <div
                  key={stage.id}
                  className="w-72 flex-shrink-0"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, stage.id)}
                >
                  {/* Stage Header */}
                  <div
                    className="rounded-t-xl p-4 text-white"
                    style={{ backgroundColor: stage.color }}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold">{stage.name}</h3>
                      <span className="bg-white/20 text-xs px-2 py-1 rounded-full">
                        {chantiers.length}
                      </span>
                    </div>
                  </div>

                  {/* Cards Container */}
                  <div className="bg-slate-100 rounded-b-xl p-3 min-h-[400px] space-y-3">
                    {chantiers.map((chantier) => (
                      <motion.div
                        key={chantier.id}
                        layout
                        draggable
                        onDragStart={(e) => handleDragStart(e, chantier, stage.id)}
                        className={`bg-white rounded-lg p-4 shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow ${
                          draggedItem?.chantier.id === chantier.id ? 'opacity-50' : ''
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <GripVertical className="w-4 h-4 text-slate-300 flex-shrink-0 mt-1" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-rm-text truncate">
                              {chantier.client_name}
                            </p>
                            
                            <p className="text-xs text-rm-text-secondary flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" />
                              {chantier.address || 'Adresse non définie'}
                              {chantier.latitude && chantier.longitude && (
                                <span className="text-green-500 ml-1" title="Position GPS configurée">
                                  GPS
                                </span>
                              )}
                            </p>

                            {chantier.chantier_type && chantier.chantier_type !== 'autre' && (
                              <p className="text-xs text-rm-text-secondary capitalize mt-1">
                                {chantier.chantier_type.replace('_', ' ')}
                              </p>
                            )}

                            {chantier.planned_start_date && (
                              <p className="text-xs text-rm-text-secondary flex items-center gap-1 mt-2">
                                <Calendar className="w-3 h-3" />
                                {formatDate(chantier.planned_start_date)}
                                {chantier.planned_end_date && ` - ${formatDate(chantier.planned_end_date)}`}
                              </p>
                            )}

                            {chantier.assigned_team && (
                              <p className="text-xs text-purple-600 flex items-center gap-1 mt-1">
                                <Users className="w-3 h-3" />
                                {chantier.assigned_team}
                              </p>
                            )}

                            <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100">
                              <div className="flex items-center gap-1">
                                {getRentabilityIcon(chantier.rentability)}
                                <span className="text-xs text-slate-500">
                                  {chantier.total_hours || 0}h
                                </span>
                              </div>
                              {chantier.quote_number && (
                                <span className="text-xs text-slate-400">
                                  {chantier.quote_number}
                                </span>
                              )}
                            </div>
                            
                            {/* Bouton Compte Rendu */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/espace-client/crm-batiment/chantiers/compte-rendu?chantier_id=${chantier.id}`);
                              }}
                              className="w-full mt-2 flex items-center justify-center gap-1 px-2 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-600 rounded-lg text-xs font-medium transition-colors"
                            >
                              <FileText size={14} />
                              Compte rendu
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}

                    {chantiers.length === 0 && (
                      <div className="text-center py-8 text-slate-400 text-sm">
                        Aucun chantier
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
        <h4 className="font-semibold text-rm-text mb-3">Étapes du chantier</h4>
        <div className="flex flex-wrap gap-4">
          {stages.map((stage, index) => (
            <div key={stage.id} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }} />
              <span className="text-sm text-rm-text-secondary">{stage.name}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-6 mt-4 pt-4 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-green-500" />
            <span className="text-xs text-rm-text-secondary">Marge haute (&gt;30%)</span>
          </div>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-amber-500" />
            <span className="text-xs text-rm-text-secondary">Marge moyenne (10-30%)</span>
          </div>
          <div className="flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-red-500" />
            <span className="text-xs text-rm-text-secondary">Marge basse (&lt;10%)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
